import {
  ArrowTrendingUpIcon,
  BuildingLibraryIcon,
  CodeBracketIcon,
  CommandLineIcon,
  CpuChipIcon,
  RocketLaunchIcon,
  StarIcon
} from '@heroicons/react/24/solid'
import type React from 'react'
import { JavaScriptLogo } from '../../../../svg/logos/index'
import { BinaryTreeLogo } from '../../../logos/BinaryTreeLogo'
import { OIJLogo } from '../../../logos/OIJLogo'
import { OILogo } from '../../../logos/OILogo'

export type Goal = {
  name: React.ReactNode
  description: React.ReactNode
  href: string
  icon: React.ReactNode
}

export const aboutLinks = [
  {
    name: 'O nas',
    href: '/o-nas'
  },
  {
    name: 'Czym jest mentoring?',
    href: '/czym-jest-mentoring'
  },
  // TODO: add news section back in when it's ready
  // {
  //   name: 'Aktualności',
  //   href: '/aktualnosci'
  // },
  {
    name: 'Nasza metoda nauczania',
    href: '/nasza-metoda-nauczania'
  },
  {
    name: 'Nasi partnerzy',
    href: '/partnerzy'
  },
  // TODO: move this page somewhere else
  // {
  //   name: 'Program stypendialny',
  //   href: '/stypendium'
  // },
  {
    name: 'Najczęściej zadawane pytania',
    href: '/faq'
  }
]

export const mentoringStaffLink = {
  name: 'Mentoring',
  href: '/mentoring'
} as const

export const manageSiteLinks = [
  {
    name: 'Portfolio nauczycieli',
    href: '/portfolia'
  },
  {
    name: 'Technologie i umiejętności',
    href: '/technologie/zarzadzaj'
  }
]

// SYNC: these slugs must match apps/algoacademy/src/app/sitemap.ts
// NOTE: order is interleaved for the row-major `grid-cols-2` menu (OfertaOptions.tsx):
// indices [0,2,4] fill the left column, [1,3,5] fill the right column (per issue #747).
export const mentoringPodstawowyGoals: Goal[] = [
  {
    name: <>Cel: Pierwsze kroki w{'\u00A0'}programowaniu</>,
    description: <>Zacznij programować od zera i{'\u00A0'}opanuj podstawy algorytmów</>,
    href: '/oferta/mentoring-podstawowy/zacznij-programowac',
    icon: <JavaScriptLogo size={24} />
  },
  {
    name: <>Cel: Topowe studia informatyczne w{'\u00A0'}Polsce</>,
    description: (
      <>
        Dostań się na{'\u00A0'}najlepsze uczelnie techniczne w{'\u00A0'}Polsce
      </>
    ),
    href: '/oferta/mentoring-podstawowy/topowe-studia-pl',
    icon: <BuildingLibraryIcon className='h-6 w-6' />
  },
  {
    name: <>Cel: Finalista Olimpiady Informatycznej Juniorów</>,
    description: (
      <>
        Trenuj systematycznie i{'\u00A0'}dotrzyj do{'\u00A0'}finału OIJ
      </>
    ),
    href: '/oferta/mentoring-podstawowy/finalista-oij',
    icon: <OIJLogo />
  },
  {
    name: <>Cel: Pierwsza praca w{'\u00A0'}IT</>,
    description: (
      <>
        Przygotuj się do{'\u00A0'}rekrutacji i{'\u00A0'}zacznij karierę jako developer
      </>
    ),
    href: '/oferta/mentoring-podstawowy/pierwsza-praca-w-it',
    icon: <BinaryTreeLogo />
  },
  {
    name: <>Cel: Finalista Olimpiady Informatycznej</>,
    description: <>Trenuj intensywnie i{'\u00A0'}stań się finalistą OI</>,
    href: '/oferta/mentoring-podstawowy/finalista-oi',
    icon: <OILogo />
  },
  {
    name: <>Cel: AI w{'\u00A0'}praktyce</>,
    description: (
      <>
        Naucz się stosować AI w{'\u00A0'}projektach i{'\u00A0'}codziennej pracy
      </>
    ),
    href: '/oferta/mentoring-podstawowy/wdrazaj-ai',
    icon: <CpuChipIcon className='h-6 w-6' />
  }
]

// NOTE: interleaved for the row-major `grid-cols-2` menu (per issue #747).
export const mentoringSpersonalizowanyGoals: Goal[] = [
  {
    name: <>Cel: Międzynarodowa Olimpiada Informatyczna Juniorów</>,
    description: <>Reprezentuj Polskę na{'\u00A0'}Międzynarodowej Olimpiadzie Juniorów</>,
    href: '/oferta/mentoring-spersonalizowany/ioi-junior',
    icon: <OIJLogo />
  },
  {
    name: <>Cel: Programista ery AI</>,
    description: <>Twórz oprogramowanie szybciej, łącząc swoje umiejętności z{'\u00A0'}AI</>,
    href: '/oferta/mentoring-spersonalizowany/programista-ery-ai',
    icon: <CommandLineIcon className='h-6 w-6' />
  },
  {
    name: <>Cel: Międzynarodowa Olimpiada Informatyczna</>,
    description: <>Reprezentuj Polskę na{'\u00A0'}Międzynarodowej Olimpiadzie Informatycznej</>,
    href: '/oferta/mentoring-spersonalizowany/ioi',
    icon: <OILogo />
  },
  {
    name: <>Cel: Awans w{'\u00A0'}pracy</>,
    description: (
      <>
        Rozwiń kompetencje techniczne i{'\u00A0'}zrób kolejny krok w{'\u00A0'}karierze
      </>
    ),
    href: '/oferta/mentoring-spersonalizowany/awans-w-pracy',
    icon: <ArrowTrendingUpIcon className='h-6 w-6' />
  },
  {
    name: <>Cel: Topowe studia zagraniczne</>,
    description: (
      <>
        Aplikuj na{'\u00A0'}ETH Zurich, MIT, Oxford i{'\u00A0'}inne najlepsze światowe uczelnie
      </>
    ),
    href: '/oferta/mentoring-spersonalizowany/studia-zagraniczne',
    icon: <BuildingLibraryIcon className='h-6 w-6' />
  },
  {
    name: <>Cel: Wybierz własny cel</>,
    description: (
      <>
        Zdefiniuj swój cel i{'\u00A0'}osiągnij go z{'\u00A0'}dedykowanym mentorem
      </>
    ),
    href: '/oferta/mentoring-spersonalizowany/wlasny-cel',
    icon: <StarIcon className='h-6 w-6' />
  }
]

// NOTE: interleaved for the row-major `grid-cols-2` menu (per issue #747).
export const mentoringProjektowyGoals: Goal[] = [
  {
    name: <>Cel: Startup technologiczny</>,
    description: (
      <>
        Zbuduj i{'\u00A0'}rozwiń swój startup technologiczny z{'\u00A0'}pomocą mentora
      </>
    ),
    href: '/oferta/mentoring-projektowy/startup',
    icon: <RocketLaunchIcon className='h-6 w-6' />
  },
  {
    name: <>Cel: Własny projekt</>,
    description: (
      <>
        Buduj i{'\u00A0'}rozwijaj swój projekt <span className='whitespace-nowrap'>- od</span>{' '}
        pomysłu do wdrożenia
      </>
    ),
    href: '/oferta/mentoring-projektowy/wlasny-projekt',
    icon: <CodeBracketIcon className='h-6 w-6' />
  },
  {
    name: <>Cel: AI w{'\u00A0'}Twoim biznesie</>,
    description: <>Wdróż AI, które udoskonali Twoją działalność</>,
    href: '/oferta/mentoring-projektowy/ai-w-biznesie',
    icon: <CpuChipIcon className='h-6 w-6' />
  },
  {
    name: <>Cel: Wybierz własny cel</>,
    description: (
      <>
        Zdefiniuj projekt i{'\u00A0'}zrealizuj go z{'\u00A0'}pomocą mentora
      </>
    ),
    href: '/oferta/mentoring-projektowy/wlasny-cel',
    icon: <StarIcon className='h-6 w-6' />
  }
]
